/**
 * Pollinations React Service - Fixed Implementation
 * Uses direct API calls instead of problematic @pollinations/react package
 */

import React from 'react';

export interface PollinationsReactImageOptions {
  width?: number;
  height?: number;
  seed?: number;
  model?: string;
  nologo?: boolean;
  enhance?: boolean;
}

export interface PollinationsReactResult {
  imageUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Generate image URL using the correct Pollinations API endpoint
 */
function generatePollinationsUrl(prompt: string, options: PollinationsReactImageOptions = {}): string {
  if (!prompt || !prompt.trim()) {
    return '';
  }

  const params = new URLSearchParams();
  if (options.width) params.append('width', options.width.toString());
  if (options.height) params.append('height', options.height.toString());
  if (options.seed) params.append('seed', options.seed.toString());
  if (options.model) params.append('model', options.model);
  if (options.nologo) params.append('nologo', 'true');
  if (options.enhance) params.append('enhance', 'true');

  // Use the CORRECT URL format that works
  const baseUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}`;
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

/**
 * Custom hook for generating character portraits (Fixed Implementation)
 */
export function useCharacterPortrait(
  shouldGenerate: boolean,
  name: string,
  archetype?: string,
  traits?: string[],
  appearanceDescription?: string,
  options: PollinationsReactImageOptions = {}
): PollinationsReactResult & { resetGeneration: () => void } {
  
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = React.useState(false);

  // Stabilize traits array to prevent re-renders
  const stableTraits = React.useMemo(() => traits || [], [traits?.join(',')]);
  
  // Build character prompt with user's appearance description
  const prompt = React.useMemo(() => {
    if (!shouldGenerate) return '';
    
    const parts = [`Portrait of ${name || 'character'}`];
    
    if (archetype && archetype.trim()) {
      parts.push(`a ${archetype}`);
    }
    
    if (appearanceDescription && appearanceDescription.trim()) {
      console.log('[PollinationsReact] Using user appearance description:', appearanceDescription);
      parts.push(appearanceDescription);
    }
    
    if (stableTraits.length > 0) {
      parts.push(stableTraits.join(', '));
    } else if (!appearanceDescription || !appearanceDescription.trim()) {
      parts.push('mysterious');
    }
    
    parts.push('half body shot from torso up, cyberpunk fantasy style, digital art, high quality, detailed face and torso, atmospheric lighting, futuristic setting');
    
    return parts.join(', ');
  }, [shouldGenerate, name, archetype, stableTraits, appearanceDescription]);

  React.useEffect(() => {
    if (shouldGenerate && prompt && !hasGenerated) {
      setIsLoading(true);
      setError(null);
      setHasGenerated(true);
      console.log('[PollinationsReact] Starting image generation with prompt:', prompt);
      
      // Generate the URL using the correct API endpoint
      const generatedUrl = generatePollinationsUrl(prompt, {
        width: options.width || 400,
        height: options.height || 400,
        seed: options.seed || Math.floor(Math.random() * 1000000),
        model: options.model || 'flux',
        nologo: true,
        enhance: true
      });
      
      console.log('[PollinationsReact] Generated URL:', generatedUrl);
      setImageUrl(generatedUrl);
      setIsLoading(false);
      setError(null);
    } else if (!shouldGenerate && hasGenerated) {
      setHasGenerated(false);
      setIsLoading(false);
    }
  }, [shouldGenerate, prompt, hasGenerated, options.width, options.height, options.seed, options.model]);

  const resetGeneration = React.useCallback(() => {
    setHasGenerated(false);
    setIsLoading(false);
    setError(null);
    setImageUrl(null);
  }, []);

  return {
    imageUrl: hasGenerated && shouldGenerate ? imageUrl : null,
    isLoading,
    error,
    resetGeneration
  };
}

/**
 * Custom hook for generating story scene images (Fixed Implementation)
 */
export function useStorySceneImage(
  imagePrompt: string,
  sceneId?: string,
  userId?: string,
  options: PollinationsReactImageOptions = {}
): PollinationsReactResult {
  
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cachedUrl, setCachedUrl] = React.useState<string | null>(null);
  const [generatedForScene, setGeneratedForScene] = React.useState<string | null>(null);
  
  // Stabilize the prompt to prevent continuous regeneration
  const stablePrompt = React.useMemo(() => {
    if (!imagePrompt || !imagePrompt.trim()) return '';
    return imagePrompt.trim();
  }, [imagePrompt]);
  
  const enhancedPrompt = React.useMemo(() => {
    if (!stablePrompt) return '';
    return `${stablePrompt}, cyberpunk fantasy, digital art, cinematic lighting, highly detailed, immersive scene, atmospheric`;
  }, [stablePrompt]);

  const shouldGenerate = sceneId && sceneId !== generatedForScene && stablePrompt !== '';

  React.useEffect(() => {
    if (shouldGenerate) {
      setIsLoading(true);
      setGeneratedForScene(sceneId);
      
      // Add a small delay to show loading state
      const timer = setTimeout(() => {
        // Generate the URL using the correct API endpoint
        const generatedUrl = generatePollinationsUrl(enhancedPrompt, {
          width: options.width || 768,
          height: options.height || 512,
          seed: options.seed || Math.floor(Math.random() * 1000000),
          model: options.model || 'flux',
          nologo: true,
          enhance: true
        });
        
        console.log('[PollinationsReact] Generated story scene URL:', generatedUrl);
        setImageUrl(generatedUrl);
        
        // Keep loading state true until image actually loads
        // The component using this hook should handle onLoad event
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldGenerate, enhancedPrompt, sceneId, options.width, options.height, options.seed, options.model]);

  // Cache the generated image in Supabase for sharing
  React.useEffect(() => {
    if (imageUrl && imageUrl !== cachedUrl && sceneId && userId) {
      const cacheImage = async () => {
        try {
          console.log('[PollinationsReact] Caching story scene image:', sceneId);
          const { storageService } = await import('./storageService');
          
          await storageService.cacheImage(`scene_${sceneId}`, imageUrl, {
            prompt: enhancedPrompt,
            userId: userId,
            type: 'story_scene',
            sceneId: sceneId,
            generatedAt: new Date()
          });
          
          setCachedUrl(imageUrl);
          console.log('[PollinationsReact] Story scene image cached successfully for sharing');
        } catch (error) {
          console.warn('[PollinationsReact] Failed to cache story scene image:', error);
        }
      };
      
      cacheImage();
    }
  }, [imageUrl, cachedUrl, sceneId, userId, enhancedPrompt]);

  return {
    imageUrl,
    isLoading,
    error
  };
}

/**
 * Custom hook for generating cartridge thumbnails (Fixed Implementation)
 */
export function useCartridgeThumbnail(
  title: string,
  description: string,
  cartridgeId?: string,
  userId?: string,
  options: PollinationsReactImageOptions = {}
): PollinationsReactResult {
  
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [cachedUrl, setCachedUrl] = React.useState<string | null>(null);
  
  const prompt = React.useMemo(() => {
    if (!title || !title.trim()) return '';
    return `Game cartridge thumbnail for "${title}", ${description}, cyberpunk RPG style, game art, digital artwork, logo design, high quality`;
  }, [title, description]);

  React.useEffect(() => {
    if (prompt) {
      setIsLoading(true);
      setError(null);
      
      // Generate the URL using the correct API endpoint
      const generatedUrl = generatePollinationsUrl(prompt, {
        width: options.width || 256,
        height: options.height || 256,
        seed: options.seed || Math.floor(Math.random() * 1000000),
        model: options.model || 'flux',
        nologo: true,
        enhance: true
      });
      
      console.log('[PollinationsReact] Generated cartridge thumbnail URL:', generatedUrl);
      setImageUrl(generatedUrl);
      setIsLoading(false);
    }
  }, [prompt, options.width, options.height, options.seed, options.model]);

  // Cache the generated thumbnail
  React.useEffect(() => {
    if (imageUrl && imageUrl !== cachedUrl && cartridgeId && userId) {
      const cacheImage = async () => {
        try {
          console.log('[PollinationsReact] Caching cartridge thumbnail:', cartridgeId);
          const { storageService } = await import('./storageService');
          
          await storageService.cacheImage(`cartridge_${cartridgeId}`, imageUrl, {
            prompt: prompt,
            userId: userId,
            type: 'cartridge_thumbnail',
            cartridgeId: cartridgeId,
            generatedAt: new Date()
          });
          
          setCachedUrl(imageUrl);
          console.log('[PollinationsReact] Cartridge thumbnail cached successfully');
        } catch (error) {
          console.warn('[PollinationsReact] Failed to cache cartridge thumbnail:', error);
        }
      };
      
      cacheImage();
    }
  }, [imageUrl, cachedUrl, cartridgeId, userId, prompt]);

  return {
    imageUrl,
    isLoading,
    error
  };
}

/**
 * Helper function to generate character portrait URL directly
 */
export function generateCharacterPortraitReact(
  name: string,
  archetype: string,
  traits: string[],
  appearanceDescription?: string,
  options: PollinationsReactImageOptions = {}
): string {
  const traitDescriptions = traits.length > 0 ? traits.join(', ') : 'mysterious';
  
  let prompt: string;
  if (appearanceDescription && appearanceDescription.trim()) {
    console.log('[PollinationsReact] Using user appearance description:', appearanceDescription);
    prompt = `Portrait of ${name}, a ${archetype}, ${appearanceDescription}, ${traitDescriptions}, cyberpunk fantasy style, digital art, high quality, detailed face, atmospheric lighting, futuristic setting`;
  } else {
    prompt = `Portrait of ${name}, a ${archetype}, ${traitDescriptions}, cyberpunk fantasy style, digital art, high quality, detailed face, atmospheric lighting, futuristic setting`;
  }

  return generatePollinationsUrl(prompt, {
    width: options.width || 400,
    height: options.height || 400,
    model: options.model || 'flux-realism'
  });
}

export default {
  useCharacterPortrait,
  useStorySceneImage,
  useCartridgeThumbnail,
  generateCharacterPortraitReact
};
